import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { of, switchMap, throwError } from 'rxjs';
import { SummaryCardComponent } from '../../shared/components/ui/summary-card/summary-card.component';
import { DashboardTableComponent } from '../../shared/components/ui/dashboard-table/dashboard-table.component';
import { LoaderComponent } from '../../shared/components/ui/loader/loader.component';
import { DocumentViewerModalComponent } from '../../shared/components/ui/document-viewer-modal/document-viewer-modal.component';
import { LettersService } from '../../shared/services/letters.service';
import { DashboardService } from '../../shared/services/dashboard.service';
import { RoutingSlipService } from '../../shared/services/routing-slip.service';
import { RoutingSlip } from '../../shared/models/routing-slip.model';
import { DocusignService } from '../../shared/services/docusign.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { LetterData } from '../../shared/models/letters.model';
import { DocumentAttachment } from '../../shared/models/routing-slip.model';
import { AuthService } from '../../core/services/auth.service';
import { UserRole, UserProfile } from '../../shared/models/auth.model';
import {
  SummaryCardData,
  DashboardTableData,
  DashboardTableConfig,
  DashboardUserData,
  DashboardItem,
  RecentUser,
  DashboardStatus,
} from '../../shared/models';
import { formatDateDDMMYYYY } from '../../shared/utils/date.util';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SummaryCardComponent,
    DashboardTableComponent,
    LoaderComponent,
    DocumentViewerModalComponent,
    ToastModule,
  ],
  providers: [LettersService, RoutingSlipService, MessageService],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  // Summary cards data
  summaryCards: SummaryCardData[] = [];

  // Recent letters data
  recentLetters: DashboardTableData[] = [];

  // Recent routing slip data
  recentRoutingSlip: DashboardTableData[] = [];

  // Recent users data
  recentUsers: DashboardUserData[] = [];

  // Table configurations
  lettersTableConfig!: DashboardTableConfig;
  routingSlipTableConfig!: DashboardTableConfig;
  usersTableConfig!: DashboardTableConfig;

  // Admin role check
  isAdmin: boolean = false;

  // Loading states
  loadingDashboardItems: boolean = false;
  loadingRecentUsers: boolean = false;
  loadingDashboard: boolean = true;

  // User profile
  userProfile: UserProfile | null = null;

  // Document viewer state
  showDocumentViewer: boolean = false;
  currentDocument: DocumentAttachment | null = null;
  documentLoading: boolean = false;

  constructor(
    private lettersService: LettersService,
    private dashboardService: DashboardService,
    private routingSlipService: RoutingSlipService,
    private docusignService: DocusignService,
    private authService: AuthService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.checkUserRole();
    this.loadDashboardData();
  }

  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }

    this.isAdmin = this.authService.hasRole(UserRole.ADMIN);
  }


  private loadDashboardData(): void {
    this.loadingDashboard = true;
    
    // Track completion of all API calls
    let completedCalls = 0;
    const totalCalls = this.isAdmin ? 4 : 3; // Admin has one extra call for users
    
    const checkAllCallsComplete = () => {
      completedCalls++;
      if (completedCalls >= totalCalls) {
        this.loadingDashboard = false;
      }
    };

    // Load summary cards data based on user role
    if (this.userProfile) {
      this.dashboardService
        .getSummaryCards(this.userProfile.role)
        .subscribe({
          next: (cards) => {
            this.summaryCards = cards;
            checkAllCallsComplete();
          },
          error: (error) => {
            this.summaryCards = [];
            checkAllCallsComplete();
          }
        });
    } else {
      checkAllCallsComplete();
    }

    // Load recent letters data using the letters service
    this.loadRecentLetters(checkAllCallsComplete);

    // Load recent routing slip data from API
    this.loadRecentRoutingSlips(checkAllCallsComplete);

    // Load recent users data from the new API (only for admin)
    if (this.isAdmin) {
      this.loadRecentUsers(checkAllCallsComplete);
    }

    // Load table configurations from dashboard service
    this.dashboardService.getTableConfigs().subscribe({
      next: (configs) => {
        this.lettersTableConfig = configs.letters;
        this.routingSlipTableConfig = configs.routingSlip;
        this.usersTableConfig = configs.users;
      },
      error: (error) => {
        // Set default configs on error
        this.lettersTableConfig = {
          title: 'Recent Letters',
          viewAllRoute: '/letters',
          columns: ['Date', 'Document Name', 'Status'],
        };
        this.routingSlipTableConfig = {
          title: 'Recent Routing Slips',
          viewAllRoute: '/routing-slip',
          columns: ['Date', 'Document Name', 'Status'],
        };
        this.usersTableConfig = {
          title: 'Recent Users',
          viewAllRoute: '/users',
          columns: ['Name', 'Email', 'Type', 'Create Date'],
        };
      }
    });
  }

  private loadRecentLetters(onComplete?: () => void): void {
    this.loadingDashboardItems = true;

    this.lettersService.getLettersWithFilters({ perPage: 5 }).subscribe({
      next: (response) => {
        // Transform letters data to table data format
        this.recentLetters = response.data.map((letter: LetterData) => ({
          uploadDate: letter.uploadDate, // Already formatted by the service
          documentName: letter.documentName,
          status: letter.status,
          id: letter.id,
          documentType: 'letter' as const,
        }));
        this.loadingDashboardItems = false;
        onComplete?.();
      },
      error: (error) => {
        this.loadingDashboardItems = false;
        // Fallback to empty array or show error message
        this.recentLetters = [];
        onComplete?.();
      },
    });
  }

  private loadRecentRoutingSlips(onComplete?: () => void): void {
    // Routing list API returns document ids; dashboard recentRoutingSlips often does not.
    this.routingSlipService.getAllRoutingSlips(1, 5, 'newest', 'all').subscribe({
      next: (response) => {
        const items = response?.items ?? [];
        this.recentRoutingSlip = items.map(
          (item: RoutingSlip & {
            Id?: string;
            statusDescription?: string;
            createdOn?: string;
          }) => ({
            uploadDate: formatDateDDMMYYYY(item.createdOn ?? item.date ?? ''),
            documentName: item.documentName ?? '',
            status: this.mapRoutingSlipDashboardStatus(item.statusDescription),
            id: item.id || item.Id,
            documentType: 'routingSlip' as const,
          })
        );
        onComplete?.();
      },
      error: () => {
        this.recentRoutingSlip = [];
        onComplete?.();
      },
    });
  }

  private mapRoutingSlipDashboardStatus(
    statusDescription?: string
  ): DashboardStatus {
    if (!statusDescription) {
      return 'Pending';
    }
    const statusLower = statusDescription.toLowerCase().trim();
    if (statusLower === 'approved') {
      return 'Approved';
    }
    if (statusLower === 'rejected') {
      return 'Rejected';
    }
    if (statusLower === 'viewed') {
      return 'Viewed';
    }
    if (statusLower === 'responded') {
      return 'Responded';
    }
    if (statusLower === 'reviewed') {
      return 'Reviewed';
    }
    return 'Pending';
  }

  private loadRecentUsers(onComplete?: () => void): void {
    this.loadingRecentUsers = true;

    this.dashboardService.getRecentUsersWithFilters({ perPage: 5 }).subscribe({
      next: (response) => {
        // Transform recent users to user data format
        this.recentUsers = this.dashboardService.transformToUserData(
          response.data
        );
        this.loadingRecentUsers = false;
        onComplete?.();
      },
      error: (error) => {
        this.loadingRecentUsers = false;
        // Fallback to empty array
        this.recentUsers = [];
        onComplete?.();
      },
    });
  }

  /**
   * Convert status number to readable text
   */
  private getStatusText(status: number): DashboardStatus {
    switch (status) {
      case 1:
        return 'Approved';
      case 2:
        return 'Rejected';
      case 3:
        return 'Pending';
      default:
        return 'Pending'; // Default to 'Pending' instead of 'Unknown' to match the interface
    }
  }

  // Recent Letters table only
  onDocumentNameClick(row: DashboardTableData): void {
    if (!row.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No document ID available for document download.',
      });
      return;
    }

    this.documentLoading = true;
    this.showDocumentViewer = true;
    this.currentDocument = null;

    this.lettersService.downloadLetterDocument(row.id).subscribe({
      next: (response: { blob: Blob; filename: string; type: string }) => {
        this.setViewerFromBlob(response.blob, response.filename, response.type);
        this.documentLoading = false;
      },
      error: () => {
        this.documentLoading = false;
        this.showDocumentViewer = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load document. Please try again.',
        });
      },
    });
  }

  // Recent Routing Slips table only
  onRoutingSlipDocumentClick(row: DashboardTableData): void {
    if (!row.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No document ID available for document download.',
      });
      return;
    }

    this.documentLoading = true;
    this.showDocumentViewer = true;
    this.currentDocument = null;
    this.openRoutingSlipDocument(row.id, row.documentName);
  }

  /**
   * Signed docs: GET /api/Docusign/download/{id}. Otherwise: GET /api/RoutingSlip/{id}/download.
   */
  private openRoutingSlipDocument(
    documentId: string,
    fallbackFileName?: string
  ): void {
    this.docusignService
      .getDownloadResponse(documentId)
      .pipe(
        switchMap((res) => {
          if (res && res.success === false) {
            return this.routingSlipService.getRoutingSlipDocumentForViewer(
              documentId
            );
          }
          try {
            return of({
              kind: 'docusign' as const,
              data: this.docusignService.parseDocusignFilePayload(res),
            });
          } catch (err) {
            return throwError(() =>
              err instanceof Error ? err : new Error(String(err))
            );
          }
        })
      )
      .subscribe({
        next: (payload) => {
          if ('kind' in payload && payload.kind === 'docusign') {
            const { blob, fileName: apiFileName } = payload.data;
            const fileName =
              apiFileName || fallbackFileName || 'document.pdf';
            const type =
              blob.type && blob.type !== 'application/octet-stream'
                ? blob.type
                : this.getFileTypeFromName(fileName);
            this.setViewerFromBlob(blob, fileName, type || 'application/pdf');
            this.documentLoading = false;
            return;
          }

          if (payload.kind === 'blob') {
            this.setViewerFromBlob(
              payload.blob,
              payload.filename,
              payload.type
            );
            this.documentLoading = false;
            return;
          }

          this.currentDocument = {
            name: payload.filename,
            size: '-',
            type: payload.type,
            url: payload.url,
            lastModified: Date.now(),
          };
          this.documentLoading = false;
        },
        error: () => {
          this.documentLoading = false;
          this.showDocumentViewer = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load document. Please try again.',
          });
        },
      });
  }

  private setViewerFromBlob(blob: Blob, fileName: string, type: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    this.currentDocument = {
      name: fileName,
      size: this.formatFileSize(blob.size),
      type,
      fileData: objectUrl,
      lastModified: Date.now(),
      blobUrl: objectUrl,
      originalBlob: blob,
    };
  }

  private getFileTypeFromName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'txt':
        return 'text/plain';
      default:
        return 'application/octet-stream';
    }
  }

  onDocumentViewerClose(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.currentDocument?.blobUrl) {
      window.URL.revokeObjectURL(this.currentDocument.blobUrl);
    }
    this.showDocumentViewer = false;
    this.currentDocument = null;
  }

  onDocumentDownload(attachment: DocumentAttachment): void {
    // If we have the original blob from the API, use it directly
    if (attachment.originalBlob) {
      // Create download link
      const url = window.URL.createObjectURL(attachment.originalBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }

  onDocumentOpenInNewTab(attachment: DocumentAttachment): void {
    // Open the document in a new tab using the blob URL
    if (attachment.blobUrl) {
      window.open(attachment.blobUrl, '_blank');
    }
  }

  /**
   * Format file size in human readable format
   * @param bytes File size in bytes
   * @returns Formatted file size string
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
